import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../modules/auth/firebaseConfig";
import M2TLogo from "../common/M2TLogo";
import { Menu, X } from "lucide-react";

export default function Header({ onShowSmartCard, activeSection }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /* ======================
     AUTH
  ====================== */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setCurrentUser);
    return () => unsubscribe();
  }, []);

  /* ======================
     BODY SCROLL LOCK
  ====================== */
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
  }, [isMenuOpen]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleHomeClick = () => {
    setIsMenuOpen(false);
    if (location.pathname === "/") {
      scrollToTop();
    } else {
      navigate("/");
      setTimeout(scrollToTop, 160);
    }
  };

  const scrollToSmartCard = () => {
    setIsMenuOpen(false);

    if (location.pathname !== "/") {
      navigate("/");
      // wait for home to mount
      setTimeout(() => {
        document
          .getElementById("smartcard-section")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } else {
      document
        .getElementById("smartcard-section")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  /* ======================
     STYLES
  ====================== */
  const navBase =
    "text-sm font-medium transition hover:underline underline-offset-4";

  const desktopText = "text-white hover:decoration-green-400";
  const mobileText = "text-gray-900";

  return (
    <>
      {/* ======================
          HEADER
      ====================== */}
      <header
        id="main-header"
        className="relative z-50 flex w-full items-center justify-between bg-white px-3 py-2 text-gray-900 shadow-sm sm:bg-[#0B0F19] sm:px-6 sm:text-white"
      >
        {/* LOGO */}
        <button
          onClick={handleHomeClick}
          className="flex items-center gap-2"
          aria-label="Million2Trillion Home"
        >
          {/* balanced mobile size */}
          <M2TLogo className="h-[1.75rem] sm:h-[2.4rem] md:h-[2.8rem]" />
        </button>

        {/* DESKTOP NAV */}

        <nav className="hidden items-center gap-6 lg:flex">
          <button
            onClick={handleHomeClick}
            className={`${navBase} ${desktopText}`}
          >
            Home
          </button>
          <button
            onClick={() => navigate("/about-us")}
            className={`${navBase} ${desktopText}`}
          >
            About Us
          </button>
          <button
            onClick={() => navigate("/solutions")}
            className={`${navBase} ${desktopText}`}
          >
            Solutions
          </button>
          <button
            onClick={scrollToSmartCard}
            className={`${navBase} ${desktopText}`}
          >
            SmartCard
          </button>

          <button
            onClick={() => navigate("/blog")}
            className={`${navBase} ${desktopText}`}
          >
            Blog
          </button>
          <button
            onClick={() => navigate("/productzones?auth=true")}
            className={`${navBase} ${desktopText}`}
          >
            Investor Access
          </button>
        </nav>

        {/* MOBILE MENU BUTTON */}
        <button
          className="text-gray-900 sm:text-white lg:hidden"
          onClick={() => setIsMenuOpen(true)}
          aria-label="Open Menu"
        >
          <Menu size={26} />
        </button>
      </header>

      {/* ======================
          MOBILE DROPDOWN MENU
      ====================== */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Menu container (NOT full screen) */}
          <div
            className="absolute left-0 right-0 z-50 bg-white text-gray-900 shadow-xl lg:hidden"
            style={{ top: "100%" }}
          >
            <nav className="flex flex-col divide-y">
              <button onClick={handleHomeClick} className="px-5 py-4 text-left">
                Home
              </button>

              <button
                onClick={() => {
                  navigate("/about-us");
                  setIsMenuOpen(false);
                }}
                className="px-5 py-4 text-left"
              >
                About Us
              </button>

              <button
                onClick={() => {
                  navigate("/solutions");
                  setIsMenuOpen(false);
                }}
                className="px-5 py-4 text-left"
              >
                Solutions
              </button>

              <button
                onClick={scrollToSmartCard}
                className="px-5 py-4 text-left"
              >
                SmartCard
              </button>

              <button
                onClick={() => {
                  navigate("/blog");
                  setIsMenuOpen(false);
                }}
                className="px-5 py-4 text-left"
              >
                Blog
              </button>

              <button
                onClick={() => {
                  navigate("/productzones?auth=true");
                  setIsMenuOpen(false);
                }}
                className="px-5 py-4 text-left font-medium"
              >
                Investor Access
              </button>
            </nav>
          </div>
        </>
      )}
    </>
  );
}
